import React from 'react';
import PropTypes from 'prop-types';
import {graphql} from 'react-apollo';

import Experiment from '@homeaway/react-experiment';
import edap from '@homeaway/edap-wrapper';

/* Molecules */
import {FallbackUI} from '../../../molecules/FallbackUI/FallbackUI';

import {ManageBookingQuery} from './ManageBookingQuery';
import translate from '../../../../utils/translations';
import TripProtectionSection from './TripProtectionSection';
import {ComponentError} from '../../../ComponentError/ComponentError';

import WithdrawBooking from './WithdrawBooking/WithdrawBooking';
import ModifyBooking from './ModifyBooking/ModifyBooking';
import Cancellation from './Cancellation/Cancellation';

import {withErrorBoundary} from '../../../ErrorBoundary/ErrorBoundary';

import actions from './allowedActions';

import {
    getCancellationInfo,
    WITHDRAWAL_CANCELLATION_TYPE,
    WITHDRAWAL_STATE,
} from './manageBookingUtils';

import './ManageBooking.less';

export const fireAndExposeChangeOfPlansEvent = (
    allowedActions,
    conversationId,
    reservationId,
    translations
) => {
    const {type} = getCancellationInfo(allowedActions, translations);
    edap().push({
        name: 'generic.event',
        data: {
            eventcategory: 'traveler activity',
            eventaction: 'manage your booking view',
            eventvalue: type,
            eventlabel: `${reservationId},${conversationId}`,
        },
    });
};

export const ManageBooking = (props) => {
    const {translations} = props;
    const ERROR_BOUNDARY_TITLE_MODIFY_BOOKING = translate(
        translations,
        'trav-dash/trav-home',
        'messages',
        'modifyBooking.request.title'
    );
    const ERROR_BOUNDARY_TITLE_CANCEL_BOOKING = translate(
        translations,
        'trav-dash/trav-home',
        'messages',
        'tripDetails.cancelBooking.title'
    );
    const ERROR_BOUNDARY_TEXT = translate(
        translations,
        'trav-dash/trav-home',
        'messages',
        'error.boundary.message'
    );

    function fetch() {
        props.data.refetch().then(props.refetch());
    }

    const renderModifyBooking = (allowedActions, policyType) => {
        const {conversationId, reservationId} = props;

        fireAndExposeChangeOfPlansEvent(
            allowedActions,
            conversationId,
            reservationId,
            translations
        );

        return withErrorBoundary(
            <ModifyBooking
                policyType={policyType}
                translations={translations}
            />,
            'modify-booking',
            <FallbackUI
                errorText={ERROR_BOUNDARY_TEXT}
                title={ERROR_BOUNDARY_TITLE_MODIFY_BOOKING}
            />
        );
    };

    const renderCancellation = (showCancelBooking, showCancellationRequest) => {
        return withErrorBoundary(
            <Cancellation
                {...props}
                fetch={fetch}
                showCancelBooking={showCancelBooking}
                showCancellationRequest={showCancellationRequest}
            />,
            'cancel-booking',
            <FallbackUI
                errorText={ERROR_BOUNDARY_TEXT}
                title={ERROR_BOUNDARY_TITLE_CANCEL_BOOKING}
            />
        );
    };

    const renderWithdrawBooking = (
        allowedActions,
        cancellationPoliciesNote
    ) => {
        const {
            conversationId,
            data: {cancellationReasons},
            shortDisplayName,
            systemData,
        } = props;
        const filteredCategory =
            cancellationReasons &&
            cancellationReasons.filter(
                (reason) => reason.category !== 'DISASTER_OR_EMERGENCY'
            );
        const {fullLocale, locale} = systemData;
        const {label} = getCancellationInfo(allowedActions, translations);

        return withErrorBoundary(
            <WithdrawBooking
                cancellationLabel={label}
                cancellationPoliciesNote={cancellationPoliciesNote}
                cancellationReasons={cancellationReasons}
                cancellationType={WITHDRAWAL_CANCELLATION_TYPE}
                conversationId={conversationId}
                fetch={fetch}
                filteredCategory={filteredCategory}
                fullLocale={fullLocale}
                locale={locale}
                shortDisplayName={shortDisplayName}
                translations={translations}
            />,
            'withdraw-booking',
            <FallbackUI
                errorText={ERROR_BOUNDARY_TEXT}
                title={ERROR_BOUNDARY_TITLE_CANCEL_BOOKING}
            />
        );
    };

    const {data, priceDetails} = props;
    if (data.loading || data.error) {
        return (
            <div
                className="CancelSection rounded-border"
                data-testid="error-wrapper"
            >
                <div className="CancelSection__section CancelSection__section--title">
                    <ComponentError
                        loading={data.loading}
                        title={translate(
                            translations,
                            'trav-dash/trav-home',
                            'messages',
                            'th.inbox.conversation.quoteSummary.cancellationPolicy'
                        )}
                        translations={translations}
                    />
                </div>
            </div>
        );
    }

    if (!data.getTravelerStay || !data.getTravelerStay.stayState) {
        return null;
    }

    const {currentStateOfStay, stateOfStayData} =
        data.getTravelerStay.stayState;
    const {cancellationPolicies, cancellationPoliciesNote, existingRequest} =
        stateOfStayData || {};

    const {cancellationPolicyPeriods, policyType} = cancellationPolicies || {};
    const policies = cancellationPolicyPeriods || [];

    const allowedActions =
        currentStateOfStay.actions?.map((action) => action.id) || [];

    // Actions allowed
    const allowsAutoCancellation = allowedActions.includes(
        actions.SELF_SERVICE_CANCEL
    );
    const allowsCancellationRequest = allowedActions.includes(
        actions.REQUEST_TO_CANCEL
    );
    const allowsBookingModificationRequest = allowedActions.includes(
        actions.REQUEST_TO_MODIFY_NEW
    );
    const allowsWithdrawal = allowedActions.includes(
        actions.REQUEST_TO_WITHDRAW
    );

    const isBookingWithdrawn = existingRequest?.state === WITHDRAWAL_STATE;

    // Sections  visible to the traveller
    const showCancellationRequest = !!existingRequest?.state;
    const showCancelBooking =
        !isBookingWithdrawn &&
        (policies.length > 0 ||
            allowsAutoCancellation ||
            allowsCancellationRequest);
    const showCancellationSection =
        showCancelBooking || showCancellationRequest;

    // Manage booking section should be visible when some action is allowed or cancellation request is present
    // or have some policies to show.
    const showManageBookingSection =
        allowedActions.length > 0 ||
        policies.length > 0 ||
        showCancellationRequest;

    return showManageBookingSection ? (
        <div
            className="CancelSection rounded-border"
            data-testid="manage-booking-wrapper"
        >
            <div
                className="CancelSection__section CancelSection__section--title"
                data-testid="manage-booking-header"
            >
                <h2>
                    {translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'th.inbox.conversation.cancel.section.title'
                    )}
                </h2>
            </div>
            {allowsBookingModificationRequest &&
                renderModifyBooking(allowedActions, policyType)}
            {showCancellationSection &&
                renderCancellation(showCancelBooking, showCancellationRequest)}
            <Experiment name={'VRBO_STX_CANCELLATION_MSGING'} variant={'on'}>
                <TripProtectionSection
                    translations={translations}
                    vasItems={priceDetails?.vasItems}
                />
            </Experiment>
            {allowsWithdrawal &&
                renderWithdrawBooking(allowedActions, cancellationPoliciesNote)}
        </div>
    ) : null;
};

export default graphql(ManageBookingQuery, {
    options: (ownProps) => ({
        errorPolicy: 'ignore',
        variables: {
            conversationUuid: ownProps.conversationId,
            currency: ownProps.systemData.currency,
            endDate: null,
            listingIds: [ownProps.listingId],
            startDate: null,
        },
        skip: !ownProps.conversationId,
    }),
})(ManageBooking);

ManageBooking.propTypes = {
    backUrl: PropTypes.string,
    conversationId: PropTypes.string.isRequired,
    departureDate: PropTypes.string,
    guestLists: PropTypes.arrayOf(
        PropTypes.shape({
            email: PropTypes.string.isRequired,
            isPrimary: PropTypes.bool.isRequired,
        })
    ),
    listingId: PropTypes.string,
    location: PropTypes.string,
    priceDetails: PropTypes.shape({}),
    reservationId: PropTypes.string,
    refetch: PropTypes.func,
    showRefundInfoBtn: PropTypes.bool,
    systemData: PropTypes.shape({}),
    translations: PropTypes.shape({}),
};

ManageBooking.defaultProps = {
    backUrl: '/traveler/th/inbox',
    refetch: () => {},
    systemData: {},
    translations: {},
};
